import {
  PREFETCH_DEFAULTS,
  type PanoVariants,
  type PrefetchFailure,
  type PrefetchReport,
  type PrefetchState,
  type PrefetchTuning,
} from '@vrlearning/lesson-schema';
import type { PrefetchItem } from './plan.js';

/**
 * Works through a prefetch plan (ADR-038, proposed; the spec's "Concurrency"): after a random part
 * of the start spread, one request at a time (two when the server says so), each read to the end so
 * the service worker keeps all of it, checked against its size and hash, and dropped. Never decodes,
 * never makes a bitmap: at most one file's bytes per request are held, and only until hashed, so the
 * pano view's "at most two photos held" is untouched. Every part that touches the world is injected,
 * so the whole of it is tested with fakes.
 */

/** The session's media signed again: `media` and the 360° photos' `variants` (ADR-041). */
export type FreshMedia = {
  media: Readonly<Record<string, string>>;
  variants: Readonly<PanoVariants>;
};

/** A response as far as the runner needs one. */
export type FetchedBody = { ok: boolean; status: number; arrayBuffer(): Promise<ArrayBuffer> };

export type RunnerDeps = {
  /** The page's fetch; `signal` aborts it when the runner is stopped (an unpair mid-request). */
  fetch: (url: string, signal?: AbortSignal) => Promise<FetchedBody>;
  /** The service worker's caches: `worlds` for a world file, `media` for the rest. */
  cache: {
    match(item: PrefetchItem): Promise<boolean>;
    delete(item: PrefetchItem): Promise<void>;
    /** The bytes the cache holds for it, if any: a late bad copy is told from a good one. */
    read?(item: PrefetchItem): Promise<ArrayBuffer | undefined>;
  };
  sleep: (ms: number) => Promise<void>;
  random: () => number;
  now: () => number;
  /** `navigator.storage.estimate`; absent where the browser has none. */
  estimate?: (() => Promise<{ quota?: number; usage?: number }>) | undefined;
  /** SHA-256 of the bytes, as lowercase hex; absent where there is no SubtleCrypto (size checked only). */
  digest?: ((bytes: ArrayBuffer) => Promise<string>) | undefined;
  /**
   * `device.mediaRefresh`: the session's media signed again. Rejects with an error whose `refused`
   * is true when the API said no; any other rejection (no answer in time) is a retry.
   */
  refresh: () => Promise<FreshMedia>;
  report: (report: PrefetchReport) => void;
  /** Not on a metered or save-data connection (worlds/cache.ts). */
  mayPrefetch: () => boolean;
  /**
   * Resolves at the connection's next change (`navigator.connection`), or when `signal` aborts
   * (stop()), its listener gone either way; absent: never asked again.
   */
  connectionChanged?: ((signal: AbortSignal) => Promise<void>) | undefined;
};

export type RunnerOptions = {
  tuning: PrefetchTuning;
  retryMs?: readonly number[];
  reportEveryMs?: number;
  refreshBeforeExpiryMs?: number;
  storageMargin?: number;
  /** How often a runner told to wait asks again. */
  pollMs?: number;
};

type Entry = {
  item: PrefetchItem;
  status: 'pending' | 'active' | 'done' | 'left';
};

/** When a signed URL's `e` (seconds since the epoch) runs out, in ms; undefined without one. */
function expiresAt(url: string): number | undefined {
  const query = url.split('?')[1];
  if (query === undefined) return undefined;
  const e = Number(new URLSearchParams(query).get('e'));
  return Number.isFinite(e) && e > 0 ? e * 1000 : undefined;
}

class SignatureRefused extends Error {}
/** A refresh that got no answer: the hub drops one asked too soon. The file is retried. */
class RefreshUnanswered extends Error {}

/** The hub answers `device.mediaRefresh` at most this often per connection, and drops the rest. */
const REFRESH_GAP_MS = 5_000;
/** A bad copy is deleted again this long after: the service worker's own write may come late. */
const BAD_COPY_AGAIN_MS = 1_000;

export class PrefetchRunner {
  readonly #deps: RunnerDeps;
  readonly #entries: Entry[];
  readonly #retryMs: readonly number[];
  readonly #reportEveryMs: number;
  readonly #refreshBeforeMs: number;
  readonly #margin: number;
  readonly #pollMs: number;
  #parallel: number;
  readonly #spreadMs: number;

  #state: PrefetchState = 'fetching';
  #failed: PrefetchFailure | undefined;
  #started = false;
  #stopped = false;
  #mayRun: () => boolean = () => true;
  #lastReport = Number.NEGATIVE_INFINITY;
  #refreshing: Promise<void> | undefined;
  /** When the last refresh was answered: none is asked again within REFRESH_GAP_MS of it. */
  #refreshedAt = Number.NEGATIVE_INFINITY;
  /** Aborted by stop(): the request in flight and any wait end at once. */
  readonly #abort = new AbortController();

  constructor(plan: readonly PrefetchItem[], deps: RunnerDeps, options: RunnerOptions) {
    this.#deps = deps;
    this.#entries = plan.map((item) => ({ item: { ...item }, status: 'pending' }));
    this.#retryMs = options.retryMs ?? PREFETCH_DEFAULTS.retryMs;
    this.#reportEveryMs = options.reportEveryMs ?? PREFETCH_DEFAULTS.reportEveryMs;
    this.#refreshBeforeMs =
      options.refreshBeforeExpiryMs ?? PREFETCH_DEFAULTS.refreshBeforeExpiryMs;
    this.#margin = options.storageMargin ?? PREFETCH_DEFAULTS.storageMargin;
    this.#pollMs = options.pollMs ?? 500;
    this.#parallel = Math.max(1, Math.min(2, Math.floor(options.tuning.parallel)));
    this.#spreadMs = Math.max(0, options.tuning.startSpreadMs);
  }

  get stopped(): boolean {
    return this.#stopped;
  }

  /** Over: ready, failed or stopped. A runner waiting for a connection is not finished. */
  get finished(): boolean {
    return this.#stopped || this.#state === 'ready' || this.#state === 'failed';
  }

  /** Some file was given up on (after its retries, or a bad copy): a reconnect may try again. */
  get hasLeft(): boolean {
    return this.#entries.some((entry) => entry.status === 'left');
  }

  get state(): PrefetchState {
    return this.#state;
  }

  /** Waits before each next item while this says no: the lesson is fetching its own step. */
  setMayRun(predicate: () => boolean): void {
    this.#mayRun = predicate;
  }

  /** At most this many requests from now on (1 beside a running lesson). */
  setParallel(parallel: number): void {
    this.#parallel = Math.max(1, Math.min(this.#parallel, Math.floor(parallel)));
  }

  /** Drops these items: the lesson fetches them itself. Not counted in the totals either. */
  skip(keys: Iterable<string>): void {
    const skipped = new Set(keys);
    for (const entry of [...this.#entries]) {
      if (entry.status === 'pending' && skipped.has(entry.item.key)) {
        this.#entries.splice(this.#entries.indexOf(entry), 1);
      }
    }
  }

  /** No further request and no further report. */
  stop(): void {
    this.#stopped = true;
    this.#abort.abort();
  }

  /** A wait that stop() cuts short. */
  #sleep(ms: number): Promise<void> {
    return this.#unlessStopped(this.#deps.sleep(ms));
  }

  /** The wait, cut short by stop(); its abort listener let go of whichever comes first. */
  #unlessStopped(wait: Promise<void>): Promise<void> {
    const signal = this.#abort.signal;
    if (signal.aborted) return Promise.resolve();
    let onAbort: () => void = () => {};
    const aborted = new Promise<void>((resolve) => {
      onAbort = (): void => resolve();
      signal.addEventListener('abort', onAbort, { once: true });
    });
    return Promise.race([wait, aborted]).finally(() =>
      signal.removeEventListener('abort', onAbort),
    );
  }

  /** The plan, worked through. Resolves with the last report, or undefined when stopped. */
  async run(): Promise<PrefetchReport | undefined> {
    if (this.#started) throw new Error('a runner runs once');
    this.#started = true;

    // Metered or save-data: said as paused, and tried again when the connection changes.
    while (!this.#deps.mayPrefetch()) {
      if (this.#state !== 'paused') this.#setState('paused');
      if (!this.#deps.connectionChanged) return this.#stopped ? undefined : this.#snapshot();
      await this.#unlessStopped(this.#deps.connectionChanged(this.#abort.signal));
      if (this.#stopped) return undefined;
    }
    // Said with the first report below.
    this.#setStateQuietly('fetching');

    // What the service worker already has is done without a request.
    // Over a copy: skip() may drop items while the cache is asked.
    for (const entry of [...this.#entries]) {
      if (!this.#entries.includes(entry)) continue;
      if (await this.#have(entry.item)) entry.status = 'done';
      if (this.#stopped) return undefined;
    }

    if (!(await this.#fits())) {
      this.#finish('failed', 'storage');
      return this.#stopped ? undefined : this.#snapshot();
    }

    this.#report(true);
    await this.#sleep(this.#deps.random() * this.#spreadMs);

    const workers = Array.from({ length: 2 }, (_, index) => this.#work(index));
    await Promise.all(workers);

    if (this.#stopped) return undefined;
    if (this.#state !== 'failed') {
      const left = this.#entries.some((entry) => entry.status === 'left');
      this.#finish(left ? 'failed' : 'ready', left ? 'network' : undefined);
    }
    return this.#snapshot();
  }

  async #work(index: number): Promise<void> {
    for (;;) {
      if (!(await this.#gate(index))) return;
      const entry = this.#entries.find((candidate) => candidate.status === 'pending');
      if (!entry) return;
      entry.status = 'active';
      // Asked again right before the request: the lesson may have fetched it meanwhile.
      if (await this.#have(entry.item)) {
        entry.status = 'done';
        this.#report(false);
        continue;
      }
      try {
        entry.status = (await this.#fetchItem(entry)) ? 'done' : 'left';
      } catch (error) {
        if (!(error instanceof SignatureRefused)) throw error;
        this.#finish('failed', 'signature');
        return;
      }
      if (entry.status === 'done') this.#report(false);
      // A piece the fresh media no longer signs was dropped meanwhile.
    }
  }

  /** Waits until this worker may take another item; false when it must not. */
  async #gate(index: number): Promise<boolean> {
    for (;;) {
      if (this.#stopped || this.#state === 'failed') return false;
      if (index >= this.#parallel) return false;
      if (!this.#mayRun()) {
        await this.#sleep(this.#pollMs);
        continue;
      }
      return true;
    }
  }

  /** One item, with its retries. True when it is in the cache, checked. */
  async #fetchItem(entry: Entry): Promise<boolean> {
    let refreshedFor403 = false;
    let attempt = 0;
    for (;;) {
      if (this.#stopped) return false;
      let outcome: 'ok' | 'retry' | 'leave' | 'forbidden' | undefined;
      const id = entry.item.id;
      const expires = id === undefined ? undefined : expiresAt(entry.item.url);
      // Near expiry: signed again first -- unless it just was; then the URL it has is tried,
      // and a 403 decides.
      if (
        expires !== undefined &&
        expires - this.#deps.now() < this.#refreshBeforeMs &&
        this.#mayRefresh()
      ) {
        const refreshed = await this.#refreshFor(entry);
        if (refreshed === 'leave') return false;
        // No answer: the URL it has still works until it expires; an expired one waits a retry.
        if (refreshed === 'retry' && expires <= this.#deps.now()) outcome = 'retry';
      }
      if (this.#stopped) return false;
      outcome ??= await this.#once(entry.item).catch(() => 'retry' as const);
      if (outcome === 'ok') return true;
      if (outcome === 'leave') return false;
      if (outcome === 'forbidden' && id !== undefined && !refreshedFor403 && this.#mayRefresh()) {
        // An expired or refused signature: signed again once, then tried at once.
        refreshedFor403 = true;
        const refreshed = await this.#refreshFor(entry);
        if (refreshed === 'leave') return false;
        if (refreshed === 'again') continue;
      }
      const wait = this.#retryMs[attempt++];
      if (wait === undefined) return false;
      await this.#sleep(wait);
    }
  }

  #mayRefresh(): boolean {
    return this.#deps.now() - this.#refreshedAt >= REFRESH_GAP_MS;
  }

  /**
   * Signs the media again for this entry's sake: 'again' to fetch it with its fresh URL, 'leave'
   * when the fresh media no longer signs it, 'retry' when nobody answered (the normal backoff).
   * A refusal throws SignatureRefused, which ends the runner.
   */
  async #refreshFor(entry: Entry): Promise<'again' | 'leave' | 'retry'> {
    try {
      await this.#refresh();
    } catch (error) {
      if (error instanceof RefreshUnanswered) return 'retry';
      throw error;
    }
    return this.#entries.includes(entry) ? 'again' : 'leave';
  }

  /** One request, read to the end, checked, dropped. */
  async #once(item: PrefetchItem): Promise<'ok' | 'retry' | 'leave' | 'forbidden'> {
    const response = await this.#deps.fetch(item.url, this.#abort.signal);
    if (response.status === 403) {
      await response.arrayBuffer().catch(() => undefined);
      return 'forbidden';
    }
    if (!response.ok) {
      await response.arrayBuffer().catch(() => undefined);
      return 'retry';
    }
    // The bytes live only inside #checked: dropped before anything else is fetched.
    const good = await this.#checked(item, await response.arrayBuffer());
    if (good) return 'ok';
    // Not what the API says this id is: out of the cache, and left for the lesson to fetch. Again
    // a second later: Workbox may put the response in the cache after the page has read it.
    await this.#deps.cache.delete(item).catch(() => undefined);
    void this.#deps
      .sleep(BAD_COPY_AGAIN_MS)
      .then(() => this.#deleteIfBad(item))
      .catch(() => undefined);
    return 'leave';
  }

  /** Deletes what the cache holds for it only when that is a bad copy: the lesson may have a good one. */
  async #deleteIfBad(item: PrefetchItem): Promise<void> {
    const held = await this.#deps.cache.read?.(item);
    if (held === undefined || (await this.#checked(item, held))) return;
    await this.#deps.cache.delete(item);
  }

  /** Whether the bytes are what the API says this file is, by size and hash where known. */
  async #checked(item: PrefetchItem, bytes: ArrayBuffer): Promise<boolean> {
    if (item.bytes !== undefined && bytes.byteLength !== item.bytes) return false;
    if (item.sha256 === undefined || !this.#deps.digest) return true;
    return (await this.#deps.digest(bytes)) === item.sha256;
  }

  /** The media signed again, one refresh at a time: a second asker waits for the first's. */
  async #refresh(): Promise<void> {
    if (this.#refreshing) return this.#refreshing;
    this.#refreshing = (async (): Promise<void> => {
      let fresh: FreshMedia;
      try {
        fresh = await this.#deps.refresh();
      } catch (error) {
        if ((error as { refused?: unknown } | undefined)?.refused === true) {
          throw new SignatureRefused('the session would not sign its media again');
        }
        throw new RefreshUnanswered('no fresh media came');
      }
      this.#refreshedAt = this.#deps.now();
      // Over a copy: skip() may drop items while an unsigned one is let go of.
      for (const entry of [...this.#entries]) {
        const id = entry.item.id;
        if (id === undefined || entry.status === 'done' || !this.#entries.includes(entry)) continue;
        const { variant } = entry.item;
        // A 360° photo's cube or light copy is signed in `variants` (ADR-041), the rest in `media`.
        const signed =
          variant === undefined
            ? undefined
            : Object.hasOwn(fresh.variants, id)
              ? fresh.variants[id]![variant]
              : undefined;
        const url = variant === undefined ? fresh.media[id] : signed?.url;
        if (signed) {
          // The file the variant names now: a photo made again has another size and hash.
          entry.item.bytes = signed.bytes;
          entry.item.sha256 = signed.sha256;
        }
        if (url === undefined) {
          // No longer this session's (a piece the school deleted, a cube being made again): not
          // fetched, and let go of.
          this.#entries.splice(this.#entries.indexOf(entry), 1);
          await this.#deps.cache.delete(entry.item).catch(() => undefined);
        } else {
          entry.item.url = url;
        }
      }
    })();
    try {
      await this.#refreshing;
    } finally {
      this.#refreshing = undefined;
    }
  }

  /**
   * Whether the cache holds it. A 360° photo's cube or light copy (ADR-041) is also checked against
   * its variant's size and hash: a photo made again (reprocessed, fetched again in 8K) keeps its
   * path, and CacheFirst would serve the old one. A copy that is not it is let go of and fetched.
   */
  async #have(item: PrefetchItem): Promise<boolean> {
    if (!(await this.#safeMatch(item))) return false;
    if (item.variant === undefined || !this.#deps.cache.read) return true;
    try {
      const held = await this.#deps.cache.read(item);
      if (held === undefined || (await this.#checked(item, held))) return true;
      await this.#deps.cache.delete(item);
      return false;
    } catch {
      return true;
    }
  }

  async #safeMatch(item: PrefetchItem): Promise<boolean> {
    try {
      return await this.#deps.cache.match(item);
    } catch {
      return false;
    }
  }

  /** Room for what is still to fetch, plus the margin; yes when the browser cannot say. */
  async #fits(): Promise<boolean> {
    if (!this.#deps.estimate) return true;
    let estimate: { quota?: number; usage?: number };
    try {
      estimate = await this.#deps.estimate();
    } catch {
      return true;
    }
    const { quota, usage } = estimate;
    if (typeof quota !== 'number' || typeof usage !== 'number') return true;
    const toFetch = this.#entries
      .filter((entry) => entry.status !== 'done')
      .reduce((sum, entry) => sum + (entry.item.bytes ?? 0), 0);
    return quota - usage >= toFetch * (1 + this.#margin);
  }

  #setStateQuietly(state: PrefetchState): void {
    this.#state = state;
  }

  #setState(state: PrefetchState): void {
    this.#state = state;
    if (this.#started) this.#report(true);
  }

  #finish(state: 'ready' | 'failed', failed?: PrefetchFailure): void {
    this.#state = state;
    this.#failed = failed;
    this.#report(true);
  }

  #report(always: boolean): void {
    if (this.#stopped) return;
    const now = this.#deps.now();
    if (!always && now - this.#lastReport < this.#reportEveryMs) return;
    this.#lastReport = now;
    this.#deps.report(this.#snapshot());
  }

  #snapshot(): PrefetchReport {
    const done = this.#entries.filter((entry) => entry.status === 'done');
    // Bytes only when every size is known: a total missing some would make a wrong percentage.
    const sized = this.#entries.every((entry) => entry.item.bytes !== undefined);
    const sum = (entries: Entry[]): number =>
      sized ? entries.reduce((total, entry) => total + (entry.item.bytes ?? 0), 0) : 0;
    const report: PrefetchReport = {
      itemsDone: done.length,
      itemsTotal: this.#entries.length,
      bytesDone: sum(done),
      bytesTotal: sum(this.#entries),
      firstStepReady: this.#entries.every(
        (entry) => entry.item.step > 1 || entry.status === 'done',
      ),
      state: this.#state,
    };
    if (this.#state === 'failed') report.failed = this.#failed ?? 'network';
    return report;
  }
}
